import { useEffect, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { verifyAppKey } from "@/lib/api";
import { Loader2, Lock, LogOut } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "mls_app_key";

export default function AppGate() {
  const [status, setStatus] = useState("checking"); // "checking" | "locked" | "unlocked"
  const [pending, setPending] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setStatus("locked");
      return;
    }
    verifyAppKey(stored)
      .then(() => setStatus("unlocked"))
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        setStatus("locked");
      });
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const k = pending.trim();
    if (!k) return;
    setSubmitting(true);
    try {
      await verifyAppKey(k);
      localStorage.setItem(STORAGE_KEY, k);
      setStatus("unlocked");
      setPending("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong password");
      setPending("");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#8a8a8a] flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (status === "locked") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] grain flex items-center justify-center px-4">
        <form onSubmit={onSubmit} className="w-full max-w-md border border-[#222] p-8" data-testid="app-gate">
          <div className="flex items-center gap-2 uppercase-mono text-[#8a8a8a]">
            <Lock size={12} strokeWidth={1.5} /> restricted
          </div>
          <h1 className="font-serif text-3xl mt-2">
            Vantage <span className="text-[#CCFF00]">Hub</span>
          </h1>
          <p className="text-sm text-[#8a8a8a] mt-3 leading-relaxed">
            This is a private workspace. Enter the access password to continue.
          </p>
          <input
            type="password"
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            placeholder="access password"
            className="w-full px-3 py-3 mt-6 font-mono text-sm"
            data-testid="app-key-input"
            autoFocus
            autoComplete="current-password"
          />
          <button
            type="submit"
            disabled={submitting}
            className="btn-accent w-full mt-4 py-3 uppercase tracking-widest text-[12px] disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="app-key-submit"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            unlock
          </button>
          <Link to="/" className="uppercase-mono text-[#555] hover:text-[#CCFF00] block mt-6 text-center">
            ← back to public site
          </Link>
        </form>
      </div>
    );
  }

  // Unlocked → render app + expose a small logout hook on window for the sidebar
  if (typeof window !== "undefined") {
    window.__mls_lock = () => {
      localStorage.removeItem(STORAGE_KEY);
      window.location.href = "/app";
    };
  }
  return <Outlet />;
}

export function LockButton() {
  return (
    <button
      onClick={() => window.__mls_lock?.()}
      data-testid="app-lock-btn"
      className="uppercase-mono text-[#8a8a8a] hover:text-[#FF3333] inline-flex items-center gap-1"
      title="Lock this workspace"
    >
      <LogOut size={12} strokeWidth={1.5} /> lock
    </button>
  );
}
